import React from 'react';
import { Button, Form, FormGroup, Input, Label } from '../../../../components/Bootstrap';
import { ButtonSelect, NobleModal } from '../../../../components';
import { MenuCategoryEditor } from '../MenuCategoryEditor';
import { Menu_menu } from '../__generated__/Menu';
import { SalesCategory, MenuCategory, SavedMenuCategory } from '../types';
import { transformCategoriesForAPI, canSubmitCategories } from '../utils';
import { MenuCategoryInput } from '../../../../globalTypes';

interface EditModalProps {
  menu: Menu_menu;
  saleCategories: SalesCategory[];
  toggle: () => void;
  onSubmit: (data: { name: string; menuCategories: MenuCategoryInput[] }) => void;
}

type SelectedTab = 'Name' | 'Categories';

const getDefaultTabForProps = (props: EditModalProps): SelectedTab => {
  return props.menu.categories.length === 0 ? 'Categories' : 'Name';
};

export const EditModal: React.FC<EditModalProps> = (props) => {
  const { onSubmit } = props;
  const [selectedTab, setSelectedTab] = React.useState<SelectedTab>(getDefaultTabForProps(props));
  const [name, setName] = React.useState(props.menu.name);

  const onChangeName = React.useCallback(
    (e) => {
      setName(e.target.value);
    },
    [setName]
  );

  const filteredCategories: SavedMenuCategory[] = React.useMemo(() => {
    return props.menu.categories.filter((category) => category.id && category.salesCategory);
  }, [props.menu.categories]);

  const [categories, setCategories] = React.useState<MenuCategory[]>(filteredCategories);
  const [hasTriedSubmitting, setHasTriedSubmitting] = React.useState(false);

  const onSubmitWrapped = React.useCallback(
    (e) => {
      e.preventDefault();

      if (!canSubmitCategories(categories)) {
        setHasTriedSubmitting(true);
        return;
      }

      onSubmit({
        name,
        menuCategories: transformCategoriesForAPI(categories),
      });
    },
    [onSubmit, name, categories]
  );

  return (
    <NobleModal size='lg' isOpen={true} toggle={props.toggle}>
      <NobleModal.Title title='Edit menu details' />
      <NobleModal.Body>
        <ButtonSelect
          type='selector'
          options={['Name', 'Categories']}
          selected={selectedTab}
          className='edit-menu-btn-select'
          onChange={setSelectedTab}
        />
        <Form onSubmit={onSubmitWrapped} className='edit-menu-category-content'>
          {selectedTab === 'Categories' && (
            <>
              <p className='text-center mt-3 mb-3'>Customize your menu with custom categories.</p>
              <MenuCategoryEditor
                saleCategories={props.saleCategories}
                categories={categories}
                onChange={setCategories}
                hasTriedSubmitting={hasTriedSubmitting}
              />
            </>
          )}
          {selectedTab === 'Name' && (
            <>
              <p className='text-center mt-3 mb-3'>Edit your menu name</p>
              <div className='mt-4'>
                <FormGroup>
                  <Label>Menu name</Label>
                  <Input onChange={onChangeName} value={name} />
                </FormGroup>
              </div>
            </>
          )}
          <div className='text-center menu-modal-btns'>
            <Button type='button' className='me-4' color='link' onClick={props.toggle}>
              Cancel
            </Button>{' '}
            <Button type='submit' color='primary'>
              Save
            </Button>
          </div>
        </Form>
      </NobleModal.Body>
    </NobleModal>
  );
};
